import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Building2, Users, DollarSign, TrendingUp, FileText, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const typeIcons = { deal: Building2, borrower: Users, lender: Users, investor: DollarSign, opportunity: TrendingUp, document: FileText, admin: Users };
const typeColors = { deal: "text-emerald-600", borrower: "text-blue-600", lender: "text-purple-600", investor: "text-amber-600", opportunity: "text-teal-600", document: "text-gray-600" };

export default function GlobalSearch({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(true); } if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const doSearch = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("ic_token");
        const res = await axios.get(`${API}/search/global?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
        setResults(res.data.results || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
  };

  const handleSelect = (r) => {
    setOpen(false);
    setQuery("");
    setResults([]);
    onNavigate?.(r.type, r.id);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#e8e0d0] bg-white/80 text-xs text-[#888] hover:border-emerald-300 transition-colors" data-testid="global-search-trigger">
        <Search className="h-3.5 w-3.5" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="hidden md:inline text-[9px] px-1.5 py-0.5 rounded bg-[#f0ebe0] text-[#999]">Ctrl+K</kbd>
      </button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 bg-black/30 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.div className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-lg z-50" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="bg-white rounded-2xl shadow-2xl border border-[#e8e0d0] overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e8e0d0]">
                  <Search className="h-4 w-4 text-[#888]" />
                  <input ref={inputRef} value={query} onChange={(e) => doSearch(e.target.value)} placeholder="Search deals, borrowers, lenders, investors..." className="flex-1 text-sm outline-none bg-transparent text-[#222] placeholder-[#bbb]" data-testid="global-search-input" />
                  <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[#faf7f2]"><X className="h-3.5 w-3.5 text-[#888]" /></button>
                </div>
                {results.length > 0 && (
                  <div className="max-h-80 overflow-y-auto py-2" data-testid="global-search-results">
                    {results.map((r, i) => {
                      const Icon = typeIcons[r.type] || Building2;
                      return (
                        <button key={`${r.type}-${r.id}-${i}`} onClick={() => handleSelect(r)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#faf7f2] transition-colors text-left" data-testid={`search-result-${r.type}-${r.id}`}>
                          <Icon className={`h-4 w-4 ${typeColors[r.type] || "text-gray-500"} shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-[#222] truncate">{r.title}</div>
                            <div className="text-[10px] text-[#999] truncate">{r.subtitle}</div>
                          </div>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#faf7f2] text-[#999] font-medium shrink-0">{r.type}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {query.length >= 2 && results.length === 0 && !loading && (
                  <div className="py-8 text-center text-xs text-[#888]">No results found for "{query}"</div>
                )}
                {loading && <div className="py-6 text-center text-xs text-[#888]">Searching...</div>}
                {query.length < 2 && !loading && (
                  <div className="py-6 px-4 text-center text-[10px] text-[#bbb]">Type 2+ characters to search across all entities</div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
